import * as React from 'react';
import ConfirmPopup from './ConfirmPopup';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import type { GenericList } from './models/JobSharedInterfaces';
import { CheckPermissions, DownloadFile, GetQuery, PostFormDataQuery, PostQuery } from './Shared';

interface SystemFile {
    id: number;
    name: string;
    type: string;
}

interface NewSystemFile {
    name: string;
    typeId: number;
    itemTypeId: number;
}

const AdminSystemFilesData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [systemFiles, setSystemFiles] = React.useState<SystemFile[]>([]);
    const [newSystemFile, setNewSystemFile] = React.useState<NewSystemFile>({
        name: "",
        typeId: -1,
        itemTypeId: -1
    });
    const [importFile, setImportFile] = React.useState<File[]>([]);
    const [systemFileTypes, setSystemFileTypes] = React.useState<GenericList[]>([]);
    const [itemTypes, setItemTypes] = React.useState<GenericList[]>([]);
    const [showAddNew, setShowAddNew] = React.useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [deleteIndex, setDeleteIndex] = React.useState(-1);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("UploadSystemFiles");
        if (hasPermission) {
            getSystemFiles();
        } else {
            navigate("/accessdenied");
        }
    }

    const getSystemFiles = async () => {
        const apiUrl = "GetSystemFiles";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setSystemFileTypes(result.data.systemFileTypes);
            setItemTypes(result.data.itemTypes);
            setSystemFiles(result.data.systemFiles);
            setLoading(false);
        }
    }

    const clearError = (errorItem: string) => {
        let errorList = errors;
        delete errorList[errorItem];

        setErrors(errorList);
    }

    const updateShowAddNew = () => {
        setShowAddNew(!showAddNew);
    }

    const handleNameChange = (e: any) => {
        e.preventDefault();

        clearError(e.target.name);
        setNewSystemFile(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleChange = (e: any) => {
        e.preventDefault();

        clearError(e.target.name);
        var value = e.target.value * 1;
        setNewSystemFile(prevState => ({ ...prevState, [e.target.name]: value }));
    }

    const handleImportFile = (e: any) => {
        delete errors["importFiles"];
        setImportFile(e.target.files);
    }


    //DELETE FILE
    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        setShowDeleteConfirm(true);
        setDeleteIndex(itemIndex);
    }

    const hideDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setDeleteIndex(-1);
    }

    const saveDeleteConfirm = () => {
        saveDeleteFile();
    }

    const saveDeleteFile = async () => {
        var fileList = [...systemFiles];
        var fileToDelete = fileList[deleteIndex];

        var deleteFile = {
            id: fileToDelete.id
        };

        var jsonItem = JSON.stringify(deleteFile);

        const apiUrl = "DeleteSystemFile";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error deleting data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("File Deleted Successfully");
                setShowDeleteConfirm(false);
                setDeleteIndex(-1);
                getSystemFiles();
            }
        }
    }
    //END DELETE FILE

    //DOWNLOAD FILE
    const downloadFile = (e: any, id: number) => {
        e.preventDefault();
        downloadSystemFile(id);
    }

    const downloadSystemFile = async (id: number) => {
        const apiUrl = 'GetSystemFile?Id=' + id;
        var filename = "systemfile.pdf";
        await DownloadFile(apiUrl, filename);
    }
    //END DOWNLOAD FILE

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;
        
        if (!newSystemFile.name) {
            errorList["name"] = "Name is required!";
            formIsValid = false;
        }

        if (newSystemFile.typeId <= 0) {
            errorList["typeId"] = "Type is required!";
            formIsValid = false;
        }

        if (importFile.length === 0) {
            errorList["importFiles"] = "Select a file to upload!";
            formIsValid = false;
        }

        setErrors(errorList)
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            saveSystemFile();
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const saveSystemFile = async () => {
        const filedata = new FormData()
        filedata.append('name', newSystemFile.name);
        filedata.append('typeId', newSystemFile.typeId.toString());
        filedata.append('itemTypeId', newSystemFile.itemTypeId.toString());
        filedata.append('file', importFile[0]);

        try {
            const apiUrl = "SaveSystemFiles";
            var result = await PostFormDataQuery(apiUrl, filedata);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("System File Saved Successfully");
                setLoading(true);
                setNewSystemFile({ name: "", typeId: -1, itemTypeId: -1 });
                getSystemFiles();
            }
        } catch (e) {
            toast.error("Error saving job file!");
        }
    }

    const confirmPopup = <ConfirmPopup heading="Delete System File?" text="Are you sure you want to delete this system file?" hideConfirmModal={hideDeleteConfirm} showConfirmModal={showDeleteConfirm} saveConfirmModal={saveDeleteConfirm} />
    
    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
            </div>

            <div className={showAddNew ? "hidden" : ""}>
                <button className="defaultbutton form-margin" onClick={updateShowAddNew} type="button">Add New System File</button>
            </div>
            <div className={showAddNew ? "" : "hidden" }>
                <div>Note: To replace a document you need to delete the existing one and upload the new document.</div>
                <div>Note: Replace documents AFTER BUSINESS HOURS otherwise there is a chance an email will go out with the incorrect attachments.</div>
                <label className="input-group" htmlFor="name">
                    <span className="label">Name</span>
                    <input type='text' id="name" name="name" className='input' value={newSystemFile.name} onChange={(e) => handleNameChange(e)}></input>
                </label>
                <span className={errors["name"] ? "label errors marginLeftLabel" : "hidden"}>{errors["name"]}</span>

                <label className="input-group" htmlFor="typeId">
                    <span className="label">Type</span>
                    <select className="select" id="typeId" name="typeId" value={newSystemFile.typeId} onChange={(e) => handleChange(e)}>
                        <option hidden defaultValue="-1" value={-1}></option>
                        {systemFileTypes.map(fileType =>
                            <option key={fileType.id} value={fileType.id}>{fileType.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["typeId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["typeId"]}</span>

                <label className="input-group" htmlFor="itemTypeId">
                    <span className="label">Item Type</span>
                    <select className="select" id="itemTypeId" name="itemTypeId" value={newSystemFile.itemTypeId} onChange={(e) => handleChange(e)}>
                        <option defaultValue="-1" value={-1}></option>
                        {itemTypes.map(itemType =>
                            <option key={itemType.id} value={itemType.id}>{itemType.name}</option>
                        )};
                    </select>
                </label>

                <div className="marginTop10">
                    <label className="input-group" htmlFor="file">
                        <span className="label">Select File </span>
                        <input type="file" className="marginBottom10" name="file" accept="application/pdf" onChange={handleImportFile} multiple />
                    </label>
                    <span className={errors["importFiles"] ? "label errors marginLeftLabel" : "hidden"}>{errors["importFiles"]}</span>
                </div>

                <div>
                    <button className="defaultbutton defaultbutton__small marginLeftLabel" onClick={save} type="button">Add</button>
                    <button className="defaultbutton defaultbutton__small marginLeft10" onClick={updateShowAddNew} type="button">Cancel</button>
                </div>
            </div>
            <hr></hr>
            <div className="overflowAuto">
                <table className="table table--nowidth tableColours">
                    <thead>
                        <tr>
                            <th className="hidden">Id</th>
                            <th>Name</th>
                            <th>Type</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    {systemFiles.map((item, index) =>
                        <tbody key={index}>
                            <tr>
                                <td className="hidden">{item.id}</td>
                                <td className="table__text--align">{item.name}</td>
                                <td className="table__text--align">{item.type}</td>
                                <td className="table__text--align">
                                    <a href="#" onClick={(e) => downloadFile(e, item.id)}>Download</a>
                                </td>
                                <td className="table__text--align">
                                    <div className="delete--tablecell">
                                        <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                            <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            <h1>System Files</h1>
            {contents}
        </div>
    );
}

export default AdminSystemFilesData;